/**
 * Client-side API helpers for brand onboarding.
 *
 *   - `crawlBrandFromWebsite()` — POST /api/brands/{id}/crawl-brand.
 *     Empty successful results stay empty; non-2xx responses throw so the
 *     caller can show an explicit recovery message.
 *   - `completeBrandOnboarding()` — PATCH /api/brands/{id} with the
 *     finalised BrandContext + `completedBrandOnboarding: true` so the
 *     middleware stops gating subsequent navigation.
 *
 * Mirrors the fetch shape used by `new-brand-form.tsx` /
 * `client-switcher.tsx`: Bearer JWT via `getBackendAuthToken(getToken)` and
 * `getApiBaseUrl()`.
 */

import { getApiBaseUrl } from '@/lib/api-config'
import type { components } from '@nex-studio/api-types'
import {
  type GetAuthToken,
  getBackendAuthToken,
} from '@/lib/backend-auth-token'
import {
  EMPTY_BRAND_CONTEXT,
  type BrandContext,
  type CompetitorEntry,
} from '@/lib/brand-context-types'

export type BrandCrawlResult = {
  brandName: string
  brandDescription: string
  brandVoices: string[]
  sellingPoints: string[]
  targetAudiences: string[]
  competitors: CompetitorEntry[]
}

const EMPTY_CRAWL_RESULT: BrandCrawlResult = {
  brandName: '',
  brandDescription: '',
  brandVoices: [],
  sellingPoints: [],
  targetAudiences: [],
  competitors: [],
}

export class BrandCrawlRateLimitedError extends Error {
  constructor() {
    super('rate_limited')
    this.name = 'BrandCrawlRateLimitedError'
  }
}

export class BrandCrawlRequestError extends Error {
  constructor(readonly status: number) {
    super(`brand_crawl_failed_${status}`)
    this.name = 'BrandCrawlRequestError'
  }
}

const PROFILE_WHITESPACE =
  '\u0009\u000a\u000b\u000c\u000d\u0020\u00a0\u1680' +
  '\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a' +
  '\u2028\u2029\u202f\u205f\u3000\ufeff'

function isProfileContentKey(key: string): boolean {
  return (
    !['schema_version', 'schemaVersion', 'language'].includes(key) &&
    !key.startsWith('_')
  )
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined || value === false) return false
  if (typeof value === 'string') {
    return [...value].some(character => !PROFILE_WHITESPACE.includes(character))
  }
  if (Array.isArray(value)) return value.some(hasMeaningfulValue)
  if (typeof value === 'object') {
    return Object.entries(value).some(
      ([key, item]) => isProfileContentKey(key) && hasMeaningfulValue(item),
    )
  }
  return true
}

export function hasMeaningfulBrandProfile(profile: unknown): boolean {
  if (!profile || typeof profile !== 'object' || Array.isArray(profile)) {
    return false
  }
  return Object.entries(profile).some(
    ([key, value]) => isProfileContentKey(key) && hasMeaningfulValue(value),
  )
}

/** Reviewable Brand Voice fields, excluding onboarding metadata and the name. */
export function hasMeaningfulBrandVoice(
  profile: Partial<BrandContext>,
): boolean {
  return hasMeaningfulBrandProfile({
    brandDescription: profile.brandDescription,
    brandVoices: profile.brandVoices,
    sellingPoints: profile.sellingPoints,
    targetAudiences: profile.targetAudiences,
    competitors: profile.competitors,
    additionalInfo: profile.additionalInfo,
  })
}

/**
 * Stateless variant — POST /api/brand-crawl/by-url. No brandId needed,
 * used during onboarding to fire the crawl in parallel with the Clerk
 * createOrganization / brand-POST chain so the user doesn't pay the
 * crawl latency on top of org setup. Returns the same shape as the
 * brand-scoped endpoint.
 */
export async function crawlBrandByUrl(
  websiteUrl: string,
  getToken: GetAuthToken,
  brandId?: string,
  language?: string,
): Promise<BrandCrawlResult> {
  const token = await getBackendAuthToken(getToken)
  // web-arch-allow-direct-fetch: one-shot POST crawl trigger, not a GET query — no useApiQuery equivalent.
  const resp = await fetch(`${getApiBaseUrl()}/api/brand-crawl/by-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      websiteUrl,
      ...(brandId ? { brandId } : {}),
      ...(language ? { language } : {}),
    }),
  })
  if (resp.status === 429) {
    throw new BrandCrawlRateLimitedError()
  }
  if (!resp.ok) {
    throw new BrandCrawlRequestError(resp.status)
  }
  const body = (await resp.json()) as {
    success: boolean
    data?: BrandCrawlResult | null
  }
  return body.data ?? EMPTY_CRAWL_RESULT
}

/**
 * Call the backend brand-crawl endpoint. Successful empty results remain
 * empty. HTTP failures throw, with a dedicated 429 type, so callers can
 * distinguish retryable capacity from other crawl failures.
 */
export async function crawlBrandFromWebsite(
  brandId: string,
  websiteUrl: string,
  getToken: GetAuthToken,
  language?: string,
): Promise<BrandCrawlResult> {
  const token = await getBackendAuthToken(getToken)
  // web-arch-allow-direct-fetch: one-shot POST crawl trigger, not a GET query — no useApiQuery equivalent.
  const resp = await fetch(
    `${getApiBaseUrl()}/api/brands/${encodeURIComponent(brandId)}/crawl-brand`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        websiteUrl,
        ...(language ? { language } : {}),
      }),
    },
  )
  if (resp.status === 429) {
    throw new BrandCrawlRateLimitedError()
  }
  if (!resp.ok) {
    throw new BrandCrawlRequestError(resp.status)
  }
  const body = (await resp.json()) as {
    success: boolean
    data?: BrandCrawlResult | null
  }
  return body.data ?? EMPTY_CRAWL_RESULT
}

export type QuickAddBrandProfileStatus =
  | 'no_website'
  | 'completed'
  | 'empty'
  | 'rate_limited'
  | 'crawl_failed'
  | 'save_failed'

export type QuickAddBrandProfileResult = {
  status: QuickAddBrandProfileStatus
  httpStatus?: number
}

export async function completeQuickAddBrandProfile({
  brandId,
  websiteUrl,
  getToken,
  language,
}: {
  brandId: string
  websiteUrl: string
  getToken: GetAuthToken
  language?: string
}): Promise<QuickAddBrandProfileResult> {
  if (!websiteUrl.trim()) return { status: 'no_website' }

  let crawl: BrandCrawlResult
  try {
    const protocol = new URL(websiteUrl).protocol
    crawl =
      protocol === 'http:'
        ? await crawlBrandFromWebsite(brandId, websiteUrl, getToken, language)
        : await crawlBrandByUrl(websiteUrl, getToken, brandId, language)
  } catch (error) {
    if (error instanceof BrandCrawlRateLimitedError) {
      return { status: 'rate_limited' }
    }
    return {
      status: 'crawl_failed',
      ...(error instanceof BrandCrawlRequestError
        ? { httpStatus: error.status }
        : {}),
    }
  }

  const brandContext: BrandContext = {
    ...EMPTY_BRAND_CONTEXT,
    brandDescription: crawl.brandDescription,
    brandVoices: crawl.brandVoices,
    sellingPoints: crawl.sellingPoints,
    targetAudiences: crawl.targetAudiences,
    competitors: crawl.competitors,
    language: language ?? '',
  }
  if (!hasMeaningfulBrandProfile(brandContext)) return { status: 'empty' }

  let resp: Response
  try {
    const token = await getBackendAuthToken(getToken)
    // web-arch-allow-direct-fetch: one-shot PATCH after crawl; the helper returns a recovery status instead of throwing into navigation.
    resp = await fetch(
      `${getApiBaseUrl()}/api/brands/${encodeURIComponent(brandId)}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          brandContext,
          completedBrandOnboarding: true,
        }),
      },
    )
  } catch {
    return { status: 'save_failed' }
  }
  if (!resp.ok) return { status: 'save_failed', httpStatus: resp.status }
  return { status: 'completed' }
}

/**
 * Fire-and-forget onboarding preflight crawl.
 *
 * Called the moment the user finishes typing their website on the first
 * onboarding screen — before the org/brand even exist. The backend
 * (org-optional `/api/brand-crawl/by-url`) crawls + extracts and caches the
 * result keyed by (user, url) so that when the brand-context wizard loads
 * moments later its own crawl call returns instantly from cache.
 *
 * `keepalive: true` lets the request survive the hard navigation that
 * follows form submit (same pattern as the sandbox prewarm in
 * onboarding-actions.ts). We don't read the response — the result lives
 * server-side in Redis. Best-effort: any failure is swallowed and the
 * wizard simply falls back to a live crawl.
 */
export function prefetchBrandCrawl(
  websiteUrl: string,
  getToken: GetAuthToken,
  language?: string,
): void {
  const trimmed = websiteUrl.trim()
  if (!trimmed) return
  const normalized = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`
  void (async () => {
    try {
      const token = await getBackendAuthToken(getToken)
      // web-arch-allow-direct-fetch: keepalive prefetch on blur; useApiQuery cannot set keepalive.
      await fetch(`${getApiBaseUrl()}/api/brand-crawl/by-url`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          websiteUrl: normalized,
          ...(language ? { language } : {}),
        }),
      })
    } catch {
      // Best-effort warm-up; the wizard falls back to a live crawl.
    }
  })()
}

/**
 * Streaming variant — POST /api/brand-crawl/by-url/stream (SSE).
 *
 * The endpoint emits four event types:
 *   - `status` — phase change (`crawling` → `extracting`)
 *   - `delta`  — incremental LLM text; caller accumulates and parses
 *                with partial-json to fill in the wizard form as
 *                tokens arrive (no need to wait for the full ~8s)
 *   - `done`   — final BrandCrawlResult; safe to overwrite progressive
 *                state with this canonical version
 *   - `error`  — `{ code: 'rate_limited' }` etc.
 *
 * Callbacks fire on the microtask queue — keep them light, the stream
 * arrives at ~100 tok/s.
 */
export async function crawlBrandByUrlStream(
  websiteUrl: string,
  getToken: GetAuthToken,
  callbacks: {
    onStatus?: (phase: 'crawling' | 'extracting') => void
    onDelta?: (accumulatedText: string, chunk: string) => void
    onDone?: (result: BrandCrawlResult) => void
    onError?: (code: string) => void
  },
  signal?: AbortSignal,
  brandId?: string,
  language?: string,
): Promise<void> {
  const token = await getBackendAuthToken(getToken)
  // web-arch-allow-direct-fetch: SSE (text/event-stream) reader, not expressible via useApiQuery.
  const resp = await fetch(`${getApiBaseUrl()}/api/brand-crawl/by-url/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      websiteUrl,
      ...(brandId ? { brandId } : {}),
      ...(language ? { language } : {}),
    }),
    signal,
  })
  if (!resp.ok || !resp.body) {
    callbacks.onError?.(`http_${resp.status}`)
    return
  }
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let accumulated = ''
  // SSE frames are separated by a blank line. The browser fetch API
  // doesn't parse them for us — we buffer until we see "\n\n", then
  // split into event/data fields per RFC.
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep = buffer.indexOf('\n\n')
    while (sep !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      sep = buffer.indexOf('\n\n')
      let event = 'message'
      let data = ''
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) data += line.slice(5).trim()
      }
      if (!data) continue
      try {
        const payload = JSON.parse(data) as Record<string, unknown>
        if (event === 'status' && typeof payload.phase === 'string') {
          callbacks.onStatus?.(payload.phase as 'crawling' | 'extracting')
        } else if (event === 'delta' && typeof payload.text === 'string') {
          accumulated += payload.text
          callbacks.onDelta?.(accumulated, payload.text)
        } else if (event === 'done') {
          callbacks.onDone?.(payload as unknown as BrandCrawlResult)
        } else if (event === 'error' && typeof payload.code === 'string') {
          callbacks.onError?.(payload.code)
        }
      } catch {
        // ignore malformed frame
      }
    }
  }
}

/**
 * Fetch a brand icon through the shared API request seam. Callers that need
 * polling should use `useApiQuery` so focus and route gating remain explicit.
 */
export type BrandIconRequest = <T = unknown>(options: {
  url: string
}) => Promise<T>

type BrandResponse = components['schemas']['BrandResponse']

export async function fetchBrandIconUrl(
  brandId: string,
  request: BrandIconRequest,
): Promise<string | null> {
  try {
    const body = await request<BrandResponse>({
      url: `/api/brands/${encodeURIComponent(brandId)}`,
    })
    return body.iconUrl ?? null
  } catch {
    return null
  }
}

/**
 * PATCH the workspace with the finalised BrandContext and flip the
 * `completedBrandOnboarding` flag. Throws on non-2xx so the form can keep
 * the user on the page (per Risks: a silently-failed submit causes a
 * middleware redirect loop because the flag stays false).
 */
export async function completeBrandOnboarding(
  brandId: string,
  context: BrandContext,
  getToken: GetAuthToken,
): Promise<void> {
  const token = await getBackendAuthToken(getToken)
  // web-arch-allow-direct-fetch: PATCH mutation, not a GET query — no useApiQuery equivalent.
  const resp = await fetch(
    `${getApiBaseUrl()}/api/brands/${encodeURIComponent(brandId)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        brandContext: context,
        completedBrandOnboarding: true,
      }),
    },
  )
  if (!resp.ok) {
    throw new Error(`PATCH workspace failed (HTTP ${resp.status})`)
  }
}
